import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import decode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import useLocalStorage from '../hooks/useLocalStorage';
import { TOKEN_KEY } from '../api';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const [currentUser, setCurrentUser] = useLocalStorage('__user__:', {});
  const navigate = useNavigate();

  const logOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('__user__:');
    setIsLoggedIn(false);
    setTokenValue('');
    navigate('/');
  }, [navigate]);

  const isTokenExpired = useCallback(
    (token) => {
      try {
        const decodedToken = decode(token);
        if (Date.now() >= decodedToken?.exp * 1000 - 60000) {
          logOut();
          return true;
        }
        return false;
      } catch (error) {
        return true;
      }
    },
    [logOut]
  );

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || isTokenExpired(token)) {
      setIsLoggedIn(false);
      setTokenValue('');
    } else {
      setIsLoggedIn(true);
      setTokenValue(token);
    }
    setLoading(false);
  }, [isTokenExpired]);

  const values = {
    isLoggedIn,
    tokenValue,
    currentUser,
    setCurrentUser,
    setLoading,
    loading,
    logOut,
  };

  return (
    <AuthContext.Provider value={values}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
